import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useT } from '../i18n/LanguageContext';

const eyebrowStyle = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '9pt',
  fontWeight: 600,
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  color: '#D6A87A',
};

const SUBNAV = [
  { label: 'Booking', target: 'booking' },
  { label: 'Timelines', target: 'timelines' },
  { label: 'Pricing', target: 'pricing' },
  { label: 'Scale', target: 'scale' },
  { label: 'Logistics', target: 'logistics' },
];

const SECTIONS = [
  { id: 'booking' },
  { id: 'timelines' },
  { id: 'pricing' },
  { id: 'scale' },
  { id: 'logistics' },
];

/* Sticky in-page sub-nav, FAQ-styled */
const FAQSubNav = () => {
  const t = useT();
  const [active, setActive] = React.useState(SUBNAV[0].target);

  React.useEffect(() => {
    const onScroll = () => {
      const offset = 140;
      let current = SUBNAV[0].target;
      for (const s of SUBNAV) {
        const el = document.getElementById(s.target);
        if (!el) continue;
        if (el.getBoundingClientRect().top - offset <= 0) current = s.target;
      }
      setActive(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleClick = (e, target) => {
    e.preventDefault();
    const el = document.getElementById(target);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  return (
    <div
      className="sticky top-0 z-40 w-full"
      style={{
        backgroundColor: '#0A132F',
        borderBottom: '1px solid rgba(214,168,122,0.35)',
        height: '60px',
      }}
      data-testid="faq-subnav"
    >
      <div className="h-full max-w-container mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center">
        <nav className="flex items-center overflow-x-auto whitespace-nowrap">
          {SUBNAV.map((l, idx) => {
            const isActive = active === l.target;
            return (
              <React.Fragment key={l.target}>
                <a
                  href={`#${l.target}`}
                  onClick={(e) => handleClick(e, l.target)}
                  data-testid={`faq-subnav-${l.target}`}
                  className="transition-colors"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '11pt',
                    fontWeight: 500,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: '#D6A87A',
                    borderBottom: isActive
                      ? '1px solid #D6A87A'
                      : '1px solid transparent',
                    paddingBottom: '4px',
                  }}
                >
                  {t(`faq.sections.${l.target}.eyebrow`)}
                </a>
                {idx < SUBNAV.length - 1 && (
                  <span
                    className="mx-3 select-none"
                    aria-hidden="true"
                    style={{ color: 'rgba(214,168,122,0.45)' }}
                  >
                    ·
                  </span>
                )}
              </React.Fragment>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

const QAPair = ({ q, a, lastInSection }) => {
  const t = useT();
  return (
  <div
    className="pt-1"
    style={{
      paddingBottom: '32px',
      marginBottom: '32px',
      borderBottom: lastInSection ? 'none' : '1px solid rgba(214,168,122,0.35)',
    }}
  >
    {/* Q */}
    <div className="flex items-baseline gap-3">
      <span
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '11pt',
          fontWeight: 600,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: '#D6A87A',
          flexShrink: 0,
        }}
      >
        {t('faq.qLabel')}
      </span>
      <p
        className="italic"
        style={{
          fontFamily: '"Cormorant Garamond", serif',
          fontStyle: 'italic',
          fontSize: '22pt',
          lineHeight: 1.3,
          fontWeight: 500,
          color: '#0A132F',
          letterSpacing: '-0.005em',
          maxWidth: '60ch',
        }}
      >
        {q}
      </p>
    </div>

    {/* A */}
    <div className="flex items-baseline gap-3 mt-5">
      <span
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '11pt',
          fontWeight: 600,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: '#D6A87A',
          flexShrink: 0,
        }}
      >
        {t('faq.aLabel')}
      </span>
      <p
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '13pt',
          fontWeight: 400,
          lineHeight: 1.7,
          color: '#4A4F63',
          maxWidth: '60ch',
        }}
      >
        {a}
      </p>
    </div>
  </div>
  );
};

const FAQ = () => {
  const t = useT();
  // Flatten all Q/A pairs across sections for JSON-LD FAQPage schema.
  const faqSchema = SECTIONS.flatMap((s) => {
    const qas = t(`faq.sections.${s.id}.qas`) || [];
    return Array.isArray(qas) ? qas.map((p) => ({ q: p.q, a: p.a })) : [];
  });
  return (
    <div data-testid="faq-page">
      <SEO
        title="FAQ, Twelve Answers | GlowMeets"
        description="Twelve questions, answered. How a booking begins, how long it takes, how we price, what we can build, how the room runs."
        keywords="GlowMeets FAQ, MICE Turkey FAQ, destination wedding FAQ, event planning Istanbul questions"
        url="/faq"
        faq={faqSchema}
      />

      {/* ───────────────────────── 1. HERO ───────────────────────── */}
      <section
        className="bg-navy w-full flex items-center justify-center"
        style={{ minHeight: '100vh', paddingTop: '120px', paddingBottom: '90px' }}
        data-testid="faq-hero"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div style={eyebrowStyle} className="mb-7">
            {t('faq.hero.eyebrow')}
          </div>
          <h1
            className="text-bone mx-auto"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: 'clamp(40px, 5vw, 64pt)',
              lineHeight: 1.05,
              fontWeight: 500,
              letterSpacing: '-0.005em',
              maxWidth: '14ch',
            }}
          >
            <span className="block">{t('faq.hero.line1')}</span>
            <span
              className="block italic"
              style={{ color: '#D6A87A', fontWeight: 400 }}
            >
              {t('faq.hero.line2')}
            </span>
          </h1>
          <p
            className="mt-7 mx-auto"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: 'italic',
              fontWeight: 400,
              fontSize: '18pt',
              color: '#D6A87A',
              lineHeight: 1.5,
              maxWidth: '50ch',
            }}
          >
            {t('faq.hero.sub')}
          </p>
          <div className="mt-12 flex justify-center">
            <span
              style={{
                display: 'block',
                width: '1px',
                height: '60px',
                backgroundColor: 'rgba(214,168,122,0.6)',
              }}
            />
          </div>
        </div>
      </section>

      {/* ───────────────────────── 2. STICKY SUBNAV ───────────────────────── */}
      <FAQSubNav />

      {/* ───────────────────────── 3. Q&A BODY ───────────────────────── */}
      <section
        className="bg-bone"
        style={{ paddingTop: '100px', paddingBottom: '100px' }}
        data-testid="faq-body"
      >
        <div className="max-w-[720px] mx-auto px-4 sm:px-6 lg:px-8">
          {SECTIONS.map(({ id }, sIdx) => {
            const section = { id, qas: t(`faq.sections.${id}.qas`) || [] };
            return (
            <div
              key={section.id}
              id={section.id}
              data-testid={`faq-section-${section.id}`}
              style={{
                paddingTop: sIdx === 0 ? '0' : '72px',
                paddingBottom: sIdx === SECTIONS.length - 1 ? '0' : '0',
              }}
            >
              <div
                style={{
                  ...eyebrowStyle,
                }}
                className="mb-5"
              >
                {t(`faq.sections.${section.id}.eyebrow`)}
              </div>

              <h2
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: 'clamp(22px, 2.2vw, 28pt)',
                  lineHeight: 1.2,
                  fontWeight: 500,
                  color: '#0A132F',
                  letterSpacing: '-0.005em',
                }}
                className="mb-5"
              >
                {t(`faq.sections.${section.id}.headline`)}
              </h2>

              <div
                style={{
                  height: '1px',
                  width: '60px',
                  backgroundColor: 'rgba(214,168,122,0.6)',
                }}
                className="mb-10"
              />

              {section.qas.map((qa, qIdx) => (
                <QAPair
                  key={qa.q}
                  q={qa.q}
                  a={qa.a}
                  lastInSection={qIdx === section.qas.length - 1}
                />
              ))}
            </div>
          );
          })}
        </div>
      </section>

      {/* ───────────────────────── 4. CLOSING BAND ───────────────────────── */}
      <section
        className="text-bone w-full text-center"
        style={{
          paddingTop: '90px',
          paddingBottom: '90px',
          background:
            'linear-gradient(180deg, #0E1B3F 0%, #0A1130 50%, #060B22 100%)',
        }}
        data-testid="faq-closing"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center">
          <img
            src="/glowmeets-mark.png"
            alt="GlowMeets compass mark"
            style={{ height: '56px', width: 'auto' }}
          />

          <p
            className="mt-7"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: 'italic',
              fontWeight: 400,
              fontSize: '14pt',
              color: 'rgba(244,239,227,0.7)',
              lineHeight: 1.5,
            }}
          >
            {t('faq.closing')}
          </p>

          <Link
            to="/contact"
            data-testid="faq-cta"
            className="inline-flex items-center justify-center mt-8 transition-colors"
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '13pt',
              fontWeight: 600,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#D6A87A',
              backgroundColor: 'transparent',
              border: '1px solid #D6A87A',
              padding: '0 28px',
              borderRadius: '999px',
              minWidth: '280px',
              height: '56px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#D6A87A';
              e.currentTarget.style.color = '#0A132F';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#D6A87A';
            }}
          >
            {t('cta.bookConsultation')}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
